import { useCallback, useEffect, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import ThoughtRecordList from "./ThoughtRecordList";
import useGetThoughtRecordData from "hooks/useGetThoughtRecordData";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
  thoughtRecordApi,
  ThoughtRecordType,
} from "api/thoughtRecordApi";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { useQuery } from "@tanstack/react-query";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import useOpenNewThoughtRecord from "hooks/useOpenNewThoughtRecord";
import useIsInappWebview from "hooks/useIsInappWebview";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectedThoughtRecordKeyAtom } from "recoil/selectedThoughtRecordAtom";
import { taskListAtom } from "../../../recoil/taskListAtom";

export default function ThoughtRecord({ taskKey }: { taskKey: string }) {
  const [searchParams] = useSearchParams();
  const currentThoughtRecordKeyParam = searchParams.get("trk"); // 앱에서만

  const patientState = useRecoilValue(patientAtom);
  const user = useRecoilValue(userAtom);
  const taskListState = useRecoilValue(taskListAtom);

  const isCoach = user?.accessToken && user?.role !== "patient";
  const navigate = useNavigate();

  const isTaskContainerSmall = useIsInappWebview();
  const [data, setData] = useState<ProgramContentType[] | undefined>();

  const [thoughtRecordList, setThoughtRecordList] = useState<
    ThoughtRecordType[] | undefined
  >();

  const [currentThoughtRecord, setCurrentThoughtRecord] = useState<
    ThoughtRecordType | undefined
  >();

  const [selectedThoughtRecordKey, setSelectedThoughtRecordKey] =
    useRecoilState(selectedThoughtRecordKeyAtom);

  const [isEmpty, setIsEmpty] = useState<boolean | undefined>(undefined);

  const openNewThoughtRecord = useOpenNewThoughtRecord();

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useGetThoughtRecordData({
    trk: currentThoughtRecord?.thoughtRecordKey,
    isFirst: isEmpty,
    enabled: !!thoughtRecordList && taskListState.length > 0,
  });

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  const patientId = patientState?.patientId; // 원시값으로 추출

  const getThoughtRecordListqueryFn = useCallback(
    () => thoughtRecordApi.getList(patientId!!),
    [patientId]
  );

  // 1. param이 있을 때 처리하는 useEffect

  useEffect(() => {
    if (!thoughtRecordList || !patientId) return;

    if (currentThoughtRecordKeyParam) {
      const selectedRecord = thoughtRecordList.find(
        (element) => element.thoughtRecordKey === currentThoughtRecordKeyParam
      );

      if (selectedRecord) {
        setCurrentThoughtRecord(selectedRecord);
      } else {
        if (
          patientId &&
          currentThoughtRecordKeyParam.includes(`${patientId}`)
        ) {
          const newThoughtRecord = {
            thoughtRecordKey: currentThoughtRecordKeyParam,
            date: new Date().toISOString(),
            situation: "",
            isHighlight: false,
            hasAiRecommendation: false,
            isDone: false,
            lastDoneAt: undefined,
            sentiment: "negative",
            shouldBeDone: false,
          } as ThoughtRecordType;

          setThoughtRecordList((prevList) => [
            newThoughtRecord,
            ...(prevList || []),
          ]);
          setCurrentThoughtRecord(newThoughtRecord);
        }
      }

      navigate(`${window.location.pathname}`, { replace: true });
    }
  }, [thoughtRecordList, currentThoughtRecordKeyParam, patientId, navigate]);

  // 2. param이 없을 때 첫 번째 기록을 선택하는 useEffect
  useEffect(() => {
    if (!thoughtRecordList || currentThoughtRecordKeyParam || isFetching)
      return;

    if (thoughtRecordList.length > 0 && !currentThoughtRecord) {
      setCurrentThoughtRecord(thoughtRecordList[0]);
    }
  }, [
    thoughtRecordList,
    currentThoughtRecordKeyParam,
    currentThoughtRecord,
    isFetching,
  ]);

  // Keep currentThoughtRecord synced with the latest list updates (e.g., after invalidations)
  useEffect(() => {
    if (!thoughtRecordList || thoughtRecordList.length === 0) return;

    const selectedKey = selectedThoughtRecordKey || currentThoughtRecord?.thoughtRecordKey;
    if (!selectedKey) return;

    const updated = thoughtRecordList.find(
      (item) => item.thoughtRecordKey === selectedKey
    );
    if (updated && !_.isEqual(updated, currentThoughtRecord)) {
      setCurrentThoughtRecord(updated);
    }
  }, [thoughtRecordList, selectedThoughtRecordKey]);

  useEffect(() => {
    if (currentThoughtRecord?.thoughtRecordKey) {
      setSelectedThoughtRecordKey(currentThoughtRecord.thoughtRecordKey!!);
    }
  }, [currentThoughtRecord]);

  useEffect(() => {
    return () => {
      setSelectedThoughtRecordKey(undefined);
    };
  }, []);

  useQuery(
    [THOUGHTRECORD_DOMAIN, THOUGHTRECORD_LIST_ENDPOINT, patientId],
    getThoughtRecordListqueryFn,
    {
      enabled: !!patientState?.patientId,
      onSuccess: (data) => {
        const mapped = data.result?.map((each) => ({
          ...each,
          shouldBeDone: each?.isDone
            ? false
            : thoughtRecordList?.find(
                (element) => element.thoughtRecordKey === each.thoughtRecordKey
              )?.shouldBeDone,
        }));

        if (!mapped || mapped.length === 0) {
          setThoughtRecordList(mapped);
          setIsEmpty(true);
          if (!currentThoughtRecord) {
            openNewThoughtRecord();
          }
        } else {
          const sorted = mapped.slice().sort(
            (a, b) => +new Date(b.date || "") - +new Date(a.date || "")
          );
          setThoughtRecordList(sorted);
          setIsEmpty(false);

          // Always sync currentThoughtRecord to the latest list after refresh
          const selectedKey =
            currentThoughtRecordKeyParam ||
            selectedThoughtRecordKey ||
            currentThoughtRecord?.thoughtRecordKey ||
            sorted[0]?.thoughtRecordKey;

          if (selectedKey) {
            const selectedIndex = sorted.findIndex(
              (element) => element.thoughtRecordKey === selectedKey
            );
            if (selectedIndex > -1) {
              setCurrentThoughtRecord(sorted[selectedIndex]);
            }
          }
        }
      },
    }
  );

  // Clear content data when switching to a different Thought Record to avoid showing stale content
  useEffect(() => {
    // When the selected TRK changes, clear the content so UI doesn't flicker with previous record's state
    // It will be re-populated once the correct fetchedData arrives
    setData(undefined);
  }, [currentThoughtRecord?.thoughtRecordKey]);

  // Apply fetched data only when it belongs to the currently selected Thought Record
  useEffect(() => {
    if (!fetchedData) return;

    const currentTrk = currentThoughtRecord?.thoughtRecordKey;
    // Some fetch payloads may include the trk; use it when available to guard against race conditions
    const anyFetched = fetchedData as any;
    const fetchedTrk = anyFetched?.thoughtRecordKey ?? anyFetched?.trk;

    // If both TRKs exist and do not match, ignore this payload (likely a late response for a previously selected record)
    if (fetchedTrk && currentTrk && fetchedTrk !== currentTrk) return;

    if (!_.isEqual(fetchedData.content, data)) {
      setData(fetchedData.content);
    }
  }, [fetchedData, currentThoughtRecord?.thoughtRecordKey]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  return (
    <>
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          taskKey={taskKey}
          thoughtRecordKey={currentThoughtRecord?.thoughtRecordKey}
          title={`생각기록지`}
          contentData={data}
          setContentData={setData}
          setMoveToIndex={setMoveToIndex}
          isDone={currentThoughtRecord?.isDone}
          lastDoneAt={currentThoughtRecord?.lastDoneAt}
          {...(isTaskContainerSmall && {
            extraContent: (
              <ThoughtRecordList
                thoughtRecordList={thoughtRecordList}
                currentThoughtRecord={currentThoughtRecord}
                setCurrentThoughtRecord={setCurrentThoughtRecord}
              />
            ),
          })}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        {!isTaskContainerSmall && (
          <ThoughtRecordList
            thoughtRecordList={thoughtRecordList}
            currentThoughtRecord={currentThoughtRecord}
            setCurrentThoughtRecord={setCurrentThoughtRecord}
          />
        )}

        <Stack sx={{ flex: 1, overflow: "hidden" }}>
          <TaskContent
            key={currentThoughtRecord?.thoughtRecordKey || 'no-trk'}
            taskKey={taskKey}
            thoughtRecordKey={currentThoughtRecord?.thoughtRecordKey}
            data={data}
            setData={setData}
            moveToIndex={moveToIndex}
            setMoveToIndex={setMoveToIndex}
            isDone={currentThoughtRecord?.isDone}
            translationVersion={fetchedData?.translationVersion}
          />
        </Stack>
      </Stack>
    </>
  );
}
